<template>
  <div v-if="canFilter" class="slide-out" :class="{ 'slide-out--visible': isVisible }">
    <div class="slide-out__toggle-button touch" @click="isVisible = !isVisible"><fa :icon="['far', 'chevron-left']" /></div>
    <div class="property-filter">
      <div class="property-filter__heading">{{ language.code === "en" ? "Filter your results by making selections below" : "ترشيح النتائج عبر الاختيارات أدناه" }}</div>
      <div :key="`inputs:${$store.state.filter.refresh}`" class="property-filter__inputs">
        <input-radio-select v-if="config.filter.propertyType && typeSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Home Type' : 'نوع المنزل'" :selections="typeSelections" v-model="inputType" is-vertical="true" />
        <input-radio-select v-if="config.filter.dwellingBeds && bedSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Bedrooms' : 'غُرْفة النَّوْم'" :selections="bedSelections" :multiple="true" v-model="inputBeds" />
        <input-radio-select v-if="config.filter.dwellingBaths && bathSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Bathrooms' : 'Bathrooms'" :selections="bathSelections" :multiple="true" v-model="inputBaths" />
        <input-radio-select v-if="config.filter.dwellingCars && carSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Cars' : 'Cars'" :selections="carSelections" :multiple="true" v-model="inputCars" />
        <input-dropdown v-if="config.filter.propertyView && viewSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'View' : 'مَنْظَر'" :selections="viewSelections" v-model="inputView" is-vertical="true" />
        <input-dropdown v-if="config.filter.propertyStatus && statusSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Status' : 'Status'" :selections="statusSelections" v-model="inputStatus" is-vertical="true" />
        <input-dropdown v-if="config.filter.dwellingDisplayName && displayNameSelections.length > 2" class="property-filter__input" :label="language.code === 'en' ? 'Apartment Type' : 'Home Designs'" :selections="displayNameSelections" v-model="inputDisplayName" is-vertical="true" />
        <template v-if="canShowLotSize">
          <label class="property-filter__label">{{ config.labels.propertySize.en }}</label>
          <input-range-select-2 :key="'lotsize' + $route.path" class="property-filter__input" :limits="lotSizeLimits" :min="lotSizeLimits[0]" :max="lotSizeLimits[1]" :step="10" units-after="m<sup>2</sup>" v-model="inputLotSize" @value-changed="inputLotSize = $event" />
        </template>
        <template v-if="canShowLotPrice">
          <label class="property-filter__label">Price</label>
          <input-range-select-2 :key="'lotprice' + $route.path" class="property-filter__input" :limits="lotPriceLimits" :min="lotPriceLimits[0]" :max="lotPriceLimits[1]" :step="1000" :format="formatPrice" v-model="inputLotPrice" @value-changed="inputLotPrice = $event" />
        </template>
        <template v-if="canShowDwellingSize">
          <label class="property-filter__label">Apartment Area</label>
          <input-range-select-2 :key="'dwellingsize' + $route.path" class="property-filter__input" :limits="dwellingSizeLimits" :min="dwellingSizeLimits[0]" :max="dwellingSizeLimits[1]" :step="10" units-after="m<sup>2</sup>" v-model="inputDwellingSize" @value-changed="inputDwellingSize = $event" />
        </template>
        <template v-if="canShowDwellingPrice">
          <label class="property-filter__label">Price</label>
          <input-range-select-2 :key="'dwellingprice' + $route.path" class="property-filter__input" :limits="dwellingPriceLimits" :min="dwellingPriceLimits[0]" :max="dwellingPriceLimits[1]" :step="1000" :format="formatPrice" v-model="inputDwellingPrice" @value-changed="inputDwellingPrice = $event" />
        </template>
        <input-filter-reset class="property-filter__input" />
      </div>
    </div>
  </div>
</template>
<script>
import InputRadioSelect from "./InputRadioSelect";
import InputDropdown from "./InputDropdown";
import InputFilterReset from "./InputFilterReset";
import InputRangeSelect2 from "./InputRangeSelect2.vue";

import { mapGetters, mapState } from "vuex";
import { isLivingEntryRoute, isLivingOptionRoute, isSubsectionOrPropertyRoute } from "@/utils/livingRoute";

export default {
  components: { InputRadioSelect, InputDropdown, InputFilterReset, InputRangeSelect2 },

  data() {
    return {
      isVisible: true,
      selectedLotSizeRange: [0, 0],
    };
  },

  computed: {
    ...mapGetters({
      language: "language",
      bedOptions: "filter/bedOptions",
      bathOptions: "filter/bathOptions",
      carOptions: "filter/carOptions",
      typeOptions: "filter/typeOptions",
      viewOptions: "filter/viewOptions",
      statusOptions: "filter/statusOptions",
      displayNameOptions: "filter/displayNameOptions",
      lotSizeLimits: "filter/lotSizeLimits",
      lotPriceLimits: "filter/lotPriceLimits",
      dwellingSizeLimits: "filter/dwellingSizeLimits",
      dwellingPriceLimits: "filter/dwellingPriceLimits",
    }),

    ...mapState({
      config: "newConfig",
      refresh: (state) => state.filter.refresh,
    }),

    bedSelections() {
      const defaultOption = { label: { en: "Any", ar: "أَيّ" }, value: false };

      const options = this.bedOptions.map((option) => {
        return { label: { en: option.toString(), ar: option.toString() }, value: parseFloat(option) };
      });
      return [defaultOption, ...options];
    },

    bathSelections() {
      const defaultOption = { label: { en: "Any", ar: "أَيّ" }, value: false };

      const options = this.bathOptions.map((option) => {
        return { label: { en: option.toString(), ar: option.toString() }, value: parseFloat(option) };
      });
      return [defaultOption, ...options];
    },

    carSelections() {
      const defaultOption = { label: { en: "Any", ar: "أَيّ" }, value: false };

      const options = this.carOptions.map((option) => {
        return { label: { en: option.toString(), ar: option.toString() }, value: parseFloat(option) };
      });
      return [defaultOption, ...options];
    },

    typeSelections() {
      const defaultOption = { label: { en: "Any", ar: "أَيّ" }, value: false };

      const options = this.typeOptions.map((option) => {
        return { label: { en: option, ar: option }, value: option };
      });

      return [defaultOption, ...options];
    },

    viewSelections() {
      const defaultOption = { label: { en: "All", ar: "أَيّ" }, value: false };

      const options = this.viewOptions.map((option) => {
        return { label: { en: option.en, ar: option.ar }, value: option };
      });

      return [defaultOption, ...options];
    },

    statusSelections() {
      const defaultOption = { label: { en: "All" }, value: false };

      const options = this.statusOptions.map((option) => {
        return { label: { en: option.en }, value: option };
      });

      return [defaultOption, ...options];
    },

    displayNameSelections() {
      const defaultOption = { label: { en: "All", ar: "أَيّ" }, value: false };

      const options = this.displayNameOptions.map((option) => {
        return { label: { en: option.en, ar: option.ar }, value: option };
      });

      return [defaultOption, ...options];
    },

    hasFilterOptions() {
      const filterConfig = this.config.filter || {};
      const isLotPriceEnabled = filterConfig.propertyLotPrice || filterConfig.propertyPriceRange;

      const hasBedFilter = filterConfig.dwellingBeds && this.bedSelections.length > 2;
      const hasBathFilter = filterConfig.dwellingBaths && this.bathSelections.length > 2;
      const hasCarFilter = filterConfig.dwellingCars && this.carSelections.length > 2;
      const hasTypeFilter = filterConfig.propertyType && this.typeSelections.length > 2;
      const hasViewFilter = filterConfig.propertyView && this.viewSelections.length > 2;
      const hasStatusFilter = filterConfig.propertyStatus && this.statusSelections.length > 2;
      const hasDisplayFilter = filterConfig.dwellingDisplayName && this.displayNameSelections.length > 2;
      const hasPropertySizeFilter = filterConfig.propertyLotSize && this.lotSizeLimits ? this.lotSizeLimits[1] > 0 : false;
      const hasPropertyPriceFilter = isLotPriceEnabled && this.lotPriceLimits ? this.lotPriceLimits[1] > 0 : false;
      const hasDwellingSizeFilter = filterConfig.dwellingSize && this.dwellingSizeLimits ? this.dwellingSizeLimits[1] > 0 : false;
      const hasDwellingPriceFilter = filterConfig.dwellingPrice && this.dwellingPriceLimits ? this.dwellingPriceLimits[1] > 0 : false;

      return hasBedFilter || hasBathFilter || hasCarFilter || hasTypeFilter || hasViewFilter || hasStatusFilter || hasDisplayFilter || hasPropertySizeFilter || hasPropertyPriceFilter || hasDwellingSizeFilter || hasDwellingPriceFilter;
    },

    canFilter() {
      if (!this.config.filter || !this.config.filter.enabled) return false;
      if (!this.hasFilterOptions) return false;

      const isProductCarouselRoute = this.$route.path.includes("/product/carousel");

      return isLivingEntryRoute(this.$route.name) || isLivingOptionRoute(this.$route.name) || isSubsectionOrPropertyRoute(this.$route.name) || isProductCarouselRoute;
    },

    inputBaths: {
      get() {
        return this.$store.state.filter.inputBaths;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputBaths: value,
        });
      },
    },

    inputBeds: {
      get() {
        return this.$store.state.filter.inputBeds;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputBeds: value,
        });
      },
    },

    inputCars: {
      get() {
        return this.$store.state.filter.inputCars;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputCars: value,
        });
      },
    },

    inputType: {
      get() {
        return this.$store.state.filter.inputType;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputType: value,
        });
      },
    },

    inputView: {
      get() {
        return this.$store.state.filter.inputView;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputView: value,
        });
      },
    },

    inputStatus: {
      get() {
        return this.$store.state.filter.inputStatus;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputStatus: value,
        });
      },
    },

    inputDisplayName: {
      get() {
        return this.$store.state.filter.inputDisplayName;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputDisplayName: value,
        });
      },
    },

    inputLotSize: {
      get() {
        return this.$store.state.filter.inputLotSize;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputLotSize: value,
        });
      },
    },

    inputLotPrice: {
      get() {
        return this.$store.state.filter.inputLotPrice;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputLotPrice: value,
        });
      },
    },

    inputDwellingSize: {
      get() {
        return this.$store.state.filter.inputDwellingSize;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputDwellingSize: value,
        });
      },
    },

    inputDwellingPrice: {
      get() {
        return this.$store.state.filter.inputDwellingPrice;
      },
      set(value) {
        this.$store.commit("filter/SET_FILTER_PARAMETERS", {
          inputDwellingPrice: value,
        });
      },
    },

    canShowLotSize() {
      return this.config.filter.propertyLotSize && this.lotSizeLimits[1] !== 0;
    },

    canShowLotPrice() {
      return (this.config.filter.propertyLotPrice || this.config.filter.propertyPriceRange) && this.lotPriceLimits[1] !== 0;
    },

    canShowDwellingSize() {
      return this.config.filter.dwellingSize && this.dwellingSizeLimits[1] !== 0;
    },

    canShowDwellingPrice() {
      return this.config.filter.dwellingPrice && this.dwellingPriceLimits[1] !== 0;
    },
  },

  watch: {
    $route() {
      const isProductCarouselRoute = this.$route.path.includes("/product/carousel");

      if (isLivingEntryRoute(this.$route.name) || isLivingOptionRoute(this.$route.name) || isSubsectionOrPropertyRoute(this.$route.name) || isProductCarouselRoute) {
        this.$nextTick(() => {
          this.reconcileFilterSelections();
        });
      }

      if (isLivingEntryRoute(this.$route.name)) {
        setTimeout(() => {
          this.isVisible = true;
        }, 500);
      }
    },
  },

  methods: {
    normalizeFilterValue(value) {
      if (typeof value === "undefined" || value === null) return "";
      return value.toString().trim().toLowerCase();
    },

    preserveMultiSelectValue(selectedValues, availableValues) {
      if (!Array.isArray(selectedValues)) return [false];
      if (!Array.isArray(availableValues) || availableValues.length <= 1) return [false];

      const availableNormalizedValues = new Set(availableValues.map((option) => this.normalizeFilterValue(option)));

      const seenValues = new Set();
      const nextValues = selectedValues
        .filter((value) => value !== false)
        .filter((value) => {
          const normalizedValue = this.normalizeFilterValue(value);
          if (!availableNormalizedValues.has(normalizedValue)) return false;
          if (seenValues.has(normalizedValue)) return false;
          seenValues.add(normalizedValue);
          return true;
        });

      if (!nextValues.length) return [false];
      return nextValues;
    },

    preserveSingleSelectValue(selectedValue, availableValues) {
      if (!selectedValue) return false;
      if (!Array.isArray(availableValues) || availableValues.length <= 1) return false;

      const normalizedSelectedValue = this.normalizeFilterValue(selectedValue);
      const matchedValue = availableValues.find((option) => this.normalizeFilterValue(option) === normalizedSelectedValue);
      return matchedValue || false;
    },

    preserveRangeValue(selectedRange, availableRange) {
      if (!Array.isArray(selectedRange) || selectedRange.length < 2) return [0, 0];
      if (!Array.isArray(availableRange) || availableRange.length < 2 || availableRange[1] === 0) return [0, 0];
      if (selectedRange[1] === 0) return [0, 0];

      const availableMin = Number(availableRange[0]);
      const availableMax = Number(availableRange[1]);

      let nextMin = Number(selectedRange[0]);
      let nextMax = Number(selectedRange[1]);

      if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax)) return [0, 0];

      nextMin = Math.min(Math.max(nextMin, availableMin), availableMax);
      nextMax = Math.min(Math.max(nextMax, availableMin), availableMax);

      if (nextMax < nextMin) {
        const temp = nextMin;
        nextMin = nextMax;
        nextMax = temp;
      }

      return [nextMin, nextMax];
    },

    reconcileFilterSelections() {
      const filterState = this.$store.state.filter;

      const nextFilterState = {
        inputBeds: this.preserveMultiSelectValue(filterState.inputBeds, this.bedOptions),
        inputBaths: this.preserveMultiSelectValue(filterState.inputBaths, this.bathOptions),
        inputCars: this.preserveMultiSelectValue(filterState.inputCars, this.carOptions),
        inputType: this.preserveMultiSelectValue(filterState.inputType, this.typeOptions),
        inputView: this.preserveSingleSelectValue(filterState.inputView, this.viewOptions.flatMap((option) => [option.en, option.ar]).filter(Boolean)),
        inputStatus: this.preserveSingleSelectValue(filterState.inputStatus, this.statusOptions.flatMap((option) => [option.en, option.ar]).filter(Boolean)),
        inputDisplayName: this.preserveSingleSelectValue(filterState.inputDisplayName, this.displayNameOptions.flatMap((option) => [option.en, option.ar]).filter(Boolean)),
        inputLotSize: this.preserveRangeValue(filterState.inputLotSize, this.lotSizeLimits),
        inputLotPrice: this.preserveRangeValue(filterState.inputLotPrice, this.lotPriceLimits),
        inputDwellingSize: this.preserveRangeValue(filterState.inputDwellingSize, this.dwellingSizeLimits),
        inputDwellingPrice: this.preserveRangeValue(filterState.inputDwellingPrice, this.dwellingPriceLimits),
        refresh: this.refresh + 1,
      };

      this.$store.commit("filter/SET_FILTER_PARAMETERS", nextFilterState);
    },

    formatPrice(price) {
      const priceInThousands = price / 1000;
      const priceInCurrency = priceInThousands.toLocaleString("en-AU", { style: "currency", currency: this.config.currency.code, maximumSignificantDigits: 10 });
      return `${priceInCurrency}K`;
    },

    resetFilter() {
      this.$store.commit("filter/SET_FILTER_PARAMETERS", {
        inputBaths: [false],
        inputBeds: [false],
        inputCars: [false],
        inputType: [false],
        inputView: false,
        inputDisplayName: false,
        inputLotSize: [0, 0],
        inputLotPrice: [0, 0],
        inputDwellingSize: [0, 0],
        inputDwellingPrice: [0, 0],
        refresh: this.refresh + 1,
      });
    },
  },
};
</script>
<style lang="scss">
.slide-out {
  background: rgba(#000, 0.8);
  border-radius: 0.5rem 0 0 0.5rem;
  bottom: 20rem;
  padding: 3rem;
  position: absolute;
  right: 32rem;
  transform: translateX(calc(100%));
  transition: transform 0.6s ease-in-out;
  width: 25rem;
  z-index: var(--filter-z-index);

  &--slider-present {
    bottom: 23.2rem;
  }

  &--visible {
    transform: none;

    svg {
      transform: scaleX(-1);
    }
  }

  svg {
    transition: transform 0.6s ease-in-out;
  }

  &__toggle-button {
    align-items: center;
    background: rgba(#000, 0.8);
    border-radius: 0.5rem 0 0 0.5rem;
    bottom: 4rem;
    display: flex;
    font-size: 2rem;
    height: 4rem;
    padding-left: 1.3rem;
    position: absolute;
    right: 100%;
    width: 4rem;
  }
}

.property-filter {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__heading {
    font-size: 1.6rem;
    line-height: 1;
    margin-bottom: 1em;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: flex-end;
  }

  &__label {
    display: block;
    font-size: 1.5rem;
    margin-bottom: 0.5em;
    text-transform: capitalize;
  }

  &__input {
    margin-bottom: 1rem;
  }
}
</style>
